import React, { useEffect, useRef, useState } from 'react'
import { CheckIcon } from '@heroicons/react/24/solid'
import { PALETA, KOLOR_DOMYSLNY, barwyDokumentu, naRgb } from '../../lib/kolorDokumentu'

/**
 * Wybór koloru firmowego dla dokumentu „VoltValue Projekt".
 *
 * ═══ PODGLĄD JEST TU NAJWAŻNIEJSZY ═══
 * Kolor trafia na pas okładki i pas ceny. Sama próbka barwy nie mówi, jak to
 * wyjdzie — dopiero pasek z prawdziwym napisem pokazuje firmie, co dostanie
 * klient. Dlatego podgląd stoi NAD wyborem, a nie pod nim.
 *
 * Napis w podglądzie bierze kolor z `tekstNaTle`, czyli dokładnie ten sam, który
 * dostanie dokument. Dzięki temu po kliknięciu żółci widać od razu, że napis
 * zrobił się ciemny — a nie dopiero po wygenerowaniu PDF-a.
 *
 * ═══ KOREKTĘ ROBI BACKEND, NIE MY ═══
 * Zdarza się rzadko: tylko odcienie, przy których ANI biel, ANI ciemny tekst nie
 * osiągają progu, czyli wąski pas szarości. Świadomie NIE powtarzamy tu tego
 * wyliczenia — dwa miejsca liczące to samo prędzej czy później zaczną się różnić.
 * Porównujemy kolor wysłany z odesłanym i mówimy wprost, że został zmieniony.
 */

const WyborKoloru = ({ kolor, busy, onZmiana }) => {
  const wybrany = (kolor || KOLOR_DOMYSLNY).toUpperCase()
  const barwy = barwyDokumentu(wybrany)

  const [wlasny, setWlasny] = useState('')
  const [skorygowany, setSkorygowany] = useState(false)
  const zadany = useRef(null)

  // Kolor zmienia się z dwóch powodów: bo ktoś kliknął próbkę albo bo wrócił
  // z zapisu. Jeżeli wrócił INNY niż wysłany, backend go przyciemnił i trzeba
  // to powiedzieć — cicha podmiana koloru marki byłaby gorsza niż jej brak.
  useEffect(() => {
    setSkorygowany(Boolean(zadany.current) && zadany.current !== wybrany)
  }, [wybrany])

  const ustawKolor = (hex) => {
    zadany.current = hex.toUpperCase()
    onZmiana(hex.toUpperCase())
  }

  const zatwierdzWlasny = () => {
    const rgb = naRgb(wlasny)
    if (rgb) ustawKolor('#' + rgb.map((k) => k.toString(16).padStart(2, '0')).join(''))
  }

  const wlasnyPoprawny = naRgb(wlasny) !== null
  const wlasnyWpisany = wlasny.trim().length > 0

  return (
    <div className="mt-6">
      <p className="text-sm font-medium text-gray-700 mb-1.5">Kolor dokumentu</p>
      <p className="text-xs text-gray-400 mb-3 leading-5">
        Nagłówek, pas ceny i akcenty w całym dokumencie. Z jednego koloru
        wyliczamy resztę odcieni, więc nie trzeba dobierać ich osobno.
      </p>

      {/* Podgląd odwzorowuje pas z okładki: to samo tło i ten sam automatycznie
          dobrany kolor napisu, więc pokazuje realny wynik, a nie próbkę barwy. */}
      <div className="rounded-xl overflow-hidden border border-gray-200 mb-4">
        <div className="px-4 py-3.5"
             style={{ backgroundColor: barwy.podstawowy, color: barwy.naPasku }}>
          <p className="font-bold text-sm leading-tight">
            Projekt instalacji fotowoltaicznej
          </p>
          <p className="text-xs mt-0.5 opacity-70">Tak wygląda nagłówek dokumentu</p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2.5"
             style={{ backgroundColor: barwy.jasny }}>
          <span className="inline-block w-8 h-1 rounded" style={{ backgroundColor: barwy.podstawowy }} />
          <span className="text-xs font-semibold" style={{ color: barwy.ciemny }}>
            Kafelki i akcenty
          </span>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {PALETA.map(({ hex, nazwa }) => {
          const aktywny = hex.toUpperCase() === wybrany
          return (
            <button
              key={hex}
              type="button"
              disabled={busy}
              onClick={() => ustawKolor(hex)}
              title={nazwa}
              aria-label={nazwa}
              aria-pressed={aktywny}
              className={`w-9 h-9 rounded-lg flex items-center justify-center transition-transform
                          hover:scale-105 disabled:cursor-not-allowed
                          ${aktywny ? 'ring-2 ring-offset-2 ring-gray-400' : ''}`}
              style={{ backgroundColor: hex }}
            >
              {/* Ptaszek w kolorze dobranym do próbki — na żółtej próbce biały
                  byłby niewidoczny i nie dałoby się poznać, co jest wybrane. */}
              {aktywny && (
                <CheckIcon className="w-5 h-5" style={{ color: barwyDokumentu(hex).naPasku }} />
              )}
            </button>
          )
        })}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <label className="text-xs text-gray-500" htmlFor="kolor-wlasny">Własny kolor</label>
        <input
          id="kolor-wlasny"
          type="text"
          value={wlasny}
          disabled={busy}
          placeholder="#0F5B70"
          onChange={(e) => setWlasny(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); zatwierdzWlasny() } }}
          className={`w-32 px-2.5 py-1.5 rounded-lg border text-sm font-mono
                      focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                      ${wlasnyWpisany && !wlasnyPoprawny ? 'border-red-300' : 'border-gray-200'}`}
        />
        <button
          type="button"
          disabled={busy || !wlasnyPoprawny}
          onClick={zatwierdzWlasny}
          className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-700
                     hover:border-blue-300 hover:text-blue-700 transition-colors
                     disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Zastosuj
        </button>
        <span className="text-xs text-gray-400 font-mono">{wybrany}</span>
      </div>

      {wlasnyWpisany && !wlasnyPoprawny && (
        <p className="mt-1.5 text-xs text-red-600">
          Wpisz kolor szesnastkowo, na przykład <span className="font-mono">#0F5B70</span>.
        </p>
      )}

      {skorygowany && (
        <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-2.5 leading-5">
          Przy tym odcieniu ani biały, ani ciemny napis nie byłby dość czytelny,
          więc przyciemniliśmy go do <span className="font-mono">{wybrany}</span>.
          Sam odcień został ten sam — zmieniła się wyłącznie jasność.
        </p>
      )}
    </div>
  )
}

export default WyborKoloru
